/**
 * Shared navigation decisions used by the legacy navigation bar and the
 * published navigation-fragment consumer. Inputs are route/merchant state;
 * outputs are deterministic decisions with no rendering or browser side effects.
 */
import {
  ROUTE_ALL,
  ROUTE_CATEGORIES_ALL,
  ROUTE_MENU_ALL,
  ROUTE_SERVICES_ALL,
  ROUTE_SHOP,
} from "@/utils/constants";
import { Category } from "@/dto/category";
import { RECOMMENDED_CATEGORY_ID } from "@/constants/itemMarkers";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { TFunction } from "@/types";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import { giftCardCategory } from "@/utils/giftCardUtils";
import type { Merchant } from "@/dto/merchant";

export type NavigationMerchantFlags = Partial<
  Pick<
    Merchant,
    | "isRetail"
    | "isService"
    | "isRestaurant"
    | "isHybrid"
    | "isEssentialMerchant"
    | "hasOnlyRetailItems"
  >
>;

export const getSearchRoute = (
  pathName: string | null,
  merchant: NavigationMerchantFlags,
  restaurantMfeEnabled = false
): { route: string; requiresHardNavigation: boolean } => {
  if (pathName?.toLocaleLowerCase().includes("shop")) {
    return { route: ROUTE_SHOP, requiresHardNavigation: false };
  }
  if (merchant.isService) {
    return { route: ROUTE_SERVICES_ALL, requiresHardNavigation: false };
  }
  if (merchant.isRestaurant) {
    return {
      route: ROUTE_MENU_ALL(restaurantMfeEnabled),
      requiresHardNavigation: true,
    };
  }
  if (merchant.isEssentialMerchant) {
    return { route: ROUTE_CATEGORIES_ALL, requiresHardNavigation: false };
  }
  return { route: ROUTE_ALL, requiresHardNavigation: false };
};

export const getShowPerks = (
  merchant: { isService?: boolean },
  merchantPreferences:
    | {
        appointmentPref?: {
          apptPayment?: string;
          cancellationNoShowFeeEnabled?: boolean;
        };
      }
    | null
    | undefined,
  depositMightApply: boolean
): boolean =>
  !merchant.isService ||
  Boolean(
    merchantPreferences?.appointmentPref?.apptPayment === "FULL" ||
      merchantPreferences?.appointmentPref?.cancellationNoShowFeeEnabled ||
      depositMightApply
  );

export const shouldShowPerksPanel = (
  isLoggedIn: boolean,
  customerPerks: unknown[] | undefined,
  showPerks: boolean,
  merchant: { isCheckoutEnabled?: boolean },
  tableData: unknown,
  isCmaApp: boolean
): boolean =>
  isLoggedIn &&
  Boolean(customerPerks?.length) &&
  showPerks &&
  Boolean(merchant.isCheckoutEnabled) &&
  !tableData &&
  !isCmaApp;

export const buildNavQueryParams = (
  scanToOrderInfo: Partial<ScanToOrderInfo> | null,
  menuName: string | null
): string => {
  let queryParams = scanToOrderInfo
    ? buildScanToOrderSearchParams(scanToOrderInfo)
    : "";

  if (menuName) {
    queryParams += queryParams ? `&menu=${menuName}` : `?menu=${menuName}`;
  }

  return queryParams;
};

interface CartAriaLabelParams {
  shouldShow: boolean;
  merchant: {
    isHybrid?: boolean;
    isService?: boolean;
    hasOnlyRetailItems?: boolean;
  };
  cartValue: number;
  storedGiftCard: unknown;
  retailCartLength: number;
  serviceCartLength: number;
  t: TFunction;
}

export const getCartAriaLabel = ({
  shouldShow,
  merchant,
  cartValue,
  storedGiftCard,
  retailCartLength,
  serviceCartLength,
  t,
}: CartAriaLabelParams): string => {
  if (!shouldShow) return t("openCart");

  if (merchant.isHybrid) {
    if (retailCartLength > 0 || storedGiftCard)
      return t("openRetailCart", { count: cartValue });
    if (serviceCartLength > 0)
      return t("openServicesCart", { count: cartValue });
  }

  if (merchant.isService) {
    if (merchant.hasOnlyRetailItems || storedGiftCard)
      return t("openRetailCart", { count: cartValue });

    return t("openServicesCart", { count: cartValue });
  }

  return t("openRetailCart", { count: cartValue });
};

export const shouldShowMoreDropdown = (
  moreCategories: Pick<Category, "name">[],
  pathName: string | null
): boolean => {
  if (moreCategories.length < 1) return false;

  const currentCategoryInPathName = decodeURIComponent(pathName ?? "").includes(
    moreCategories[0]?.name
  );

  return !(moreCategories.length === 1 && currentCategoryInPathName);
};

export const insertUncategorisedCategory = <T extends Category>(
  categories: T[],
  uncategorisedItems: { numberOfItems?: number } | undefined,
  uncategorized: T
): T[] => {
  if (
    !uncategorisedItems ||
    uncategorisedItems.numberOfItems === 0 ||
    categories.some((category) => category.id === uncategorized.id)
  ) {
    return categories;
  }

  return [...categories, uncategorized];
};

export const insertRecommendedCategory = <T extends Category>(
  categories: T[],
  recommendedCategory: T | undefined
): T[] => {
  if (
    !recommendedCategory ||
    categories.some((category) => category.id === recommendedCategory.id)
  ) {
    return categories;
  }

  return [recommendedCategory, ...categories];
};

export const getMappedNavigationCategories = <T extends Category>(
  categories: T[],
  navigationCategories: { value: string }[] | undefined
): T[] => {
  const isConfigured =
    Array.isArray(navigationCategories) && navigationCategories.length > 0;

  if (!isConfigured) {
    return categories
      .filter((category) => category.id !== giftCardCategory.id)
      .slice(0, 5);
  }

  const mappedCategories = navigationCategories
    .map((orderedCategory) =>
      categories.find((category) => category.id === orderedCategory.value)
    )
    .filter(
      (category): category is T =>
        category !== undefined && category.id !== RECOMMENDED_CATEGORY_ID
    );

  const recommendedCategory = categories.find(
    (category) => category.id === RECOMMENDED_CATEGORY_ID
  );
  if (!recommendedCategory) return mappedCategories;

  return [recommendedCategory, ...mappedCategories].slice(0, 5);
};
